import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  TextField
} from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CloseIcon from '@material-ui/icons/Close';
import { ValidatedNewSerialNoPayload } from 'src/types';
import { slices, useAppDispatch } from 'src/redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { Typography } from '@material-ui/core';
import { useSnackBar } from 'src/hooks';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';

interface Props {
  isOpen: boolean;
  onHandleClose: () => void;
  transactionId?: string;
  currentSerialNo?: string;
  productName?: string;
  branch: number;
  ecOrderId?: string;
  getTransaction: () => void;
  getTransactionPayments: () => void;
  onFailedChange?: (value: boolean) => void;
}

const useStyles = makeStyles(() => ({
  MainBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'left',
    alignItems: 'left',
    width: '100%'
  },
  detailsBox: {
    textAlign: 'left'
  },
  textfieldBox: {
    width: '100%',
    maxWidth: 'auto',
    textAlign: 'left',
    marginTop: '1rem'
  }
}));

const { actions: transactionActions } = slices.transaction;

const component: FC<Props> = ({
  isOpen,
  onHandleClose,
  transactionId,
  currentSerialNo,
  productName,
  branch,
  ecOrderId,
  getTransaction,
  getTransactionPayments,
  onFailedChange
}) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isValidSerialNo, setIsValidSerialNo] = useState<boolean>(false);
  const [isAddingItem, setIsAddingItem] = useState<boolean>(false);
  const [isVoidingItem, setIsVoidingItem] = useState<boolean>(false);

  // eslint-disable-next-line no-unused-vars
  const [errorCheck, setErrorCheck] = useState<boolean>(false);
  const [errorValidationCheck, setErrorValidationCheck] = useState<boolean>(
    false
  );
  const [addErrorCheck, setAddErrorCheck] = useState<boolean>(false);
  const [voidErrorCheck, setVoidErrorCheck] = useState<boolean>(false);

  const changeTransactionSerialNo = useCallback(
    async (payload: ValidatedNewSerialNoPayload): Promise<void> => {
      if (isLoading || isAddingItem || isVoidingItem) return;

      setIsLoading(true);

      try {
        const res = unwrapResult(
          await dispatch(transactionActions.validateNewSerialNoThunk(payload))
        );

        if (res.success) {
          setIsValidSerialNo(true);
          snackbar.show({
            severity: 'success',
            message: res.message
          });

          const addResult = unwrapResult(
            await dispatch(
              transactionActions.addItemToTransactionNoThunk({
                ec_order_id: ecOrderId,
                branch_id: [branch],
                mode: 'add',
                product_name: productName,
                serial_no: payload.new_serial_no,
                transaction_no: transactionId
              })
            )
          );

          if (addResult.success) {
            setIsAddingItem(true);
            snackbar.show({
              severity: 'success',
              message: `New Serial No. ${payload.new_serial_no} added successfully!`
            });

            const voidResult = unwrapResult(
              await dispatch(
                transactionActions.voidItemTransaction({
                  serial_no: currentSerialNo,
                  transaction_no: transactionId,
                  void_reason: `Void due to change serial no to ${payload.new_serial_no}`
                })
              )
            );

            if (voidResult.success) {
              setIsVoidingItem(true);
              snackbar.show({
                severity: 'success',
                message: `Old Serial No. ${currentSerialNo} voided successfully!`
              });

              getTransaction();
              getTransactionPayments();
              onHandleClose();
              if (onFailedChange) onFailedChange(true);
            } else {
              setVoidErrorCheck(true);
              snackbar.show({
                severity: 'error',
                message: voidResult?.message || 'Failed to void old serial no.'
              });
              if (onFailedChange) onFailedChange(true);
            }
          } else {
            setAddErrorCheck(true);
            snackbar.show({
              severity: 'error',
              message: addResult?.message || 'Failed to add new serial no.'
            });
            if (onFailedChange) onFailedChange(true);
          }
        } else {
          setErrorValidationCheck(true);
          const error_keys = Object.keys(res.errors || {});
          snackbar.show({
            severity: 'error',
            message:
              res.errors?.[error_keys[0]] || `Failed to validate Serial No.`
          });
          if (onFailedChange) onFailedChange(true);
        }
      } catch (err) {
        setErrorValidationCheck(true);
        setAddErrorCheck(true);
        setVoidErrorCheck(true);
        snackbar.show({
          severity: 'error',
          message: 'An unexpected error occurred.'
        });
        console.error('Error during changeTransactionSerialNo:', err);
        if (onFailedChange) onFailedChange(true);
      } finally {
        setIsLoading(false);
      }
    },
    [
      isLoading,
      isAddingItem,
      isVoidingItem,
      dispatch,
      transactionId,
      productName,
      branch,
      ecOrderId,
      currentSerialNo,
      getTransaction,
      getTransactionPayments,
      onFailedChange,
      onHandleClose,
      snackbar
    ]
  );

  const handleResetIcon = () => {
    setErrorValidationCheck(false);
    setAddErrorCheck(false);
    setVoidErrorCheck(false);
    setIsValidSerialNo(false);
    setIsAddingItem(false);
    setIsVoidingItem(false);
  };

  const formik = useFormik({
    initialValues: {
      current_serial_no: currentSerialNo || '',
      new_serial_no: '',
      transaction_no: transactionId || ''
    },
    validationSchema: Yup.object({
      new_serial_no: Yup.string().required('New Serial No is required')
    }),
    onSubmit: (_, { resetForm }) => {
      setIsLoading(true);

      const payload: ValidatedNewSerialNoPayload = {
        new_serial_no: formik.values.new_serial_no,
        current_serial_no: currentSerialNo || '',
        transaction_no: transactionId || ''
      };

      changeTransactionSerialNo(payload);

      resetForm();
    }
  });

  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (isLoading) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    [isLoading]
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth={'sm'}
      onClose={() => {
        if (isLoading || isAddingItem || isVoidingItem) {
          return;
        }
        onHandleClose();
      }}
    >
      <IconButton
        style={{ position: 'absolute', top: 0, right: 0 }}
        aria-label="close"
        color="secondary"
        onClick={onHandleClose}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle id="form-dialog-title">CHANGING SERIAL NUMBER</DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <Box className={classes.MainBox}>
                <Typography>{`From`}</Typography>

                <Box className={classes.detailsBox}>
                  <Typography variant="h5">{currentSerialNo}</Typography>

                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Product Name:</strong> {productName ?? '--'}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Branch ID:</strong>{' '}
                    {Array.isArray(branch) ? branch.join(', ') : branch ?? '--'}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Order Ref No:</strong> {ecOrderId ?? '--'}
                  </Typography>
                </Box>

                <Box className={classes.textfieldBox}>
                  <Typography
                    style={{ marginBottom: '10px' }}
                  >{`To`}</Typography>

                  <TextField
                    fullWidth
                    variant="outlined"
                    id="new_serial_no"
                    name="new_serial_no"
                    label="NEW SERIAL NO."
                    value={formik.values.new_serial_no}
                    onChange={(e) => {
                      handleResetIcon();
                      formik.handleChange(e);
                    }}
                    onFocus={handleResetIcon}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.new_serial_no &&
                      Boolean(formik.errors.new_serial_no)
                    }
                    helperText={
                      formik.touched.new_serial_no &&
                      formik.errors.new_serial_no
                    }
                  />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={12}>
              <Box display="flex" flexDirection="column" style={{ gap: 5 }}>
                <Box display="flex" alignItems="center" style={{ gap: 5 }}>
                  {errorValidationCheck ? (
                    <ErrorIcon style={{ color: 'red' }} />
                  ) : (
                    <CheckCircleIcon
                      style={{
                        color: isValidSerialNo ? 'green' : '#DCDCDC'
                      }}
                    />
                  )}
                  <Typography
                    style={{
                      color: errorValidationCheck ? 'red' : '#000'
                    }}
                  >
                    New Serial No. Valid
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                  {addErrorCheck ? (
                    <ErrorIcon style={{ color: 'red' }} />
                  ) : (
                    <CheckCircleIcon
                      style={{
                        color: isAddingItem ? 'green' : '#DCDCDC'
                      }}
                    />
                  )}
                  <Typography
                    style={{
                      color: addErrorCheck ? 'red' : '#000'
                    }}
                  >
                    Adding New Serial No.
                  </Typography>
                  {addErrorCheck ||
                    (errorCheck && (
                      <Typography variant="caption">
                        Manual add the new serial number.
                      </Typography>
                    ))}
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                  {voidErrorCheck ? (
                    <ErrorIcon style={{ color: 'red' }} />
                  ) : (
                    <CheckCircleIcon
                      style={{
                        color: isVoidingItem ? 'green' : '#DCDCDC'
                      }}
                    />
                  )}
                  <Typography
                    style={{
                      color: voidErrorCheck ? 'red' : '#000'
                    }}
                  >
                    Voiding Old Serial No.
                  </Typography>
                  {voidErrorCheck ||
                    (errorCheck && (
                      <Typography variant="caption">
                        Manual Void the old serial number. {currentSerialNo}
                      </Typography>
                    ))}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={12}>
              <Button
                variant="contained"
                fullWidth
                color="primary"
                type="submit"
                style={{ marginBottom: '1rem' }}
                disabled={isLoading || isAddingItem || isVoidingItem}
              >
                {isLoading ? (
                  <CircularProgress style={{ color: 'white' }} />
                ) : (
                  'Change Serial No.'
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ChangeSerialNoDialog = memo(component);
